import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ShieldAlert, Search, Wrench, Crown, AlertTriangle, CheckCircle, RefreshCcw, Image as ImageIcon, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * One-screen admin health-check panel that wraps every diagnostic +
 * healing endpoint in 1-click buttons so the operator never has to
 * paste URLs into the browser or use curl/Postman. Replaces the
 * "go hit /api/admin/founding-creators/audit in your address bar"
 * workflow.
 *
 * Three sections, each a self-contained card:
 *   1. Founding Creator Flag Health (audit + backfill)
 *   2. Creator Full-State Diagnostic (lookup by email)
 *   3. House Products Hero Image Health (status + regenerate)
 */
function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

function Card({ title, subtitle, icon: Icon, children, accent = '#00F0FF' }) {
  return (
    <div
      className="bg-neutral-900/70 border rounded-2xl p-5 md:p-6 backdrop-blur-sm"
      style={{ borderColor: `${accent}33` }}
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}44` }}
        >
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg md:text-xl font-bold text-white">{title}</h3>
          {subtitle && <p className="text-xs md:text-sm text-neutral-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function StatPill({ label, value, color = 'neutral' }) {
  const palette = {
    good: 'text-green-400 bg-green-500/10 border-green-500/30',
    warn: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
    bad: 'text-red-400 bg-red-500/10 border-red-500/30',
    info: 'text-[#00F0FF] bg-[#00F0FF]/10 border-[#00F0FF]/30',
    neutral: 'text-neutral-300 bg-neutral-800/60 border-white/10',
  };
  return (
    <div className={`px-3 py-2 rounded-lg border ${palette[color]} text-center`}>
      <div className="text-2xl font-black">{value ?? '—'}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">{label}</div>
    </div>
  );
}

function FoundingCreatorSection() {
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);

  const runAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/founding-creators/audit`, {
        headers: authHeaders(),
      });
      setAuditData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const runBackfill = useCallback(async () => {
    if (!window.confirm(
      `This will grant the Founding Creator flag to ${auditData?.missing_founding_flag || 0} creator(s). Continue?`
    )) return;
    setBackfillLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/founding-creators/backfill`, {}, {
        headers: authHeaders(),
      });
      toast.success(
        `Backfill complete: ${res.data.granted} granted · ${res.data.capped_no_seats_left} capped · ${res.data.seats_remaining} seats left`
      );
      runAudit(); // Refresh
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  }, [auditData, runAudit]);

  useEffect(() => { runAudit(); }, [runAudit]);

  return (
    <Card
      title="Founding Creator Flag Health"
      subtitle="Audits which approved VIPs are missing the is_founding_creator flag (causes Standard 5% to show on their dashboard instead of 0% starts on launch day). Backfill grants the flag to anyone missing it."
      icon={Crown}
      accent="#FFD700"
    >
      {auditLoading && !auditData ? (
        <div className="flex items-center gap-2 text-neutral-400 py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading audit…
        </div>
      ) : auditData ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
            <StatPill label="Approved VIPs" value={auditData.approved_vip_count} color="info" />
            <StatPill label="Has Flag ✓" value={auditData.has_founding_flag} color="good" />
            <StatPill
              label="Missing Flag"
              value={auditData.missing_founding_flag}
              color={auditData.missing_founding_flag > 0 ? 'warn' : 'good'}
            />
            <StatPill label="Not Signed Up Yet" value={auditData.no_user_account_yet} color="neutral" />
          </div>

          {auditData.missing_founding_flag > 0 ? (
            <>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-semibold text-yellow-300">
                    {auditData.missing_founding_flag} creator{auditData.missing_founding_flag === 1 ? '' : 's'} need{auditData.missing_founding_flag === 1 ? 's' : ''} the founding flag
                  </span>
                </div>
                <ul className="text-xs text-neutral-300 space-y-1 max-h-40 overflow-y-auto" data-testid="missing-flag-list">
                  {auditData.missing_flag_users?.map((u) => (
                    <li key={u.user_id} className="flex justify-between gap-2 py-1 border-b border-white/5 last:border-0">
                      <span className="truncate">
                        <span className="text-white font-medium">{u.name || '—'}</span>
                        <span className="text-neutral-500 ml-2">{u.email}</span>
                      </span>
                      <span className="text-neutral-500 shrink-0">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={runBackfill}
                  disabled={backfillLoading}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                  data-testid="founding-creator-backfill-btn"
                >
                  {backfillLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                  Backfill {auditData.missing_founding_flag} Founding Creator{auditData.missing_founding_flag === 1 ? '' : 's'}
                </Button>
                <Button
                  variant="outline"
                  onClick={runAudit}
                  disabled={auditLoading}
                  className="border-white/20"
                >
                  <RefreshCcw className={`w-4 h-4 mr-2 ${auditLoading ? 'animate-spin' : ''}`} />
                  Re-run Audit
                </Button>
              </div>
            </>
          ) : (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
              <div className="text-sm text-green-300">
                All signed-up VIP creators have the Founding Creator flag. Nothing to fix.
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runAudit}
                  className="ml-3 border-white/20 text-xs"
                >
                  Re-run audit
                </Button>
              </div>
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
}

function CreatorDiagnosticSection() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setLoading(true);
    setState(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/creator/full-state?email=${encodeURIComponent(trimmed)}`,
        { headers: authHeaders() }
      );
      setState(res.data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const Field = ({ label, value, ok, warn, mono }) => (
    <div className="flex items-start gap-2 py-1.5 border-b border-white/5 last:border-0 text-sm">
      <span className="text-neutral-400 w-44 shrink-0">{label}</span>
      <span className={`flex-1 break-all ${mono ? 'font-mono text-xs' : ''} ${ok ? 'text-green-400' : warn ? 'text-yellow-400' : 'text-white'}`}>
        {value === null || value === undefined || value === ''
          ? <span className="text-neutral-600 italic">not set</span>
          : (typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value))}
      </span>
    </div>
  );

  return (
    <Card
      title="Creator Diagnostic — Look up a Creator"
      subtitle="Paste any creator's email to see their full server-side state: role, Stripe connection, founding flag, products count, and any Stripe-required actions blocking them from accepting payments."
      icon={Search}
      accent="#00F0FF"
    >
      <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="creator@example.com"
          className="bg-neutral-800 border-white/10 flex-1"
          data-testid="creator-diagnostic-email"
        />
        <Button
          type="submit"
          disabled={loading || !email.trim()}
          className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold"
          data-testid="creator-diagnostic-lookup-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          Look Up
        </Button>
      </form>

      {state && !state.found && (
        <div className="bg-neutral-800/60 border border-white/10 rounded-lg p-4 text-sm text-neutral-300">
          No user account found for <strong className="text-white">{state.email}</strong>. They may not have signed up yet, or you may have a typo.
        </div>
      )}

      {state?.found && (
        <div className="space-y-5">
          <div>
            <h4 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-2">User</h4>
            {state.duplicate_user_accounts_count > 1 && (
              <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <div className="text-xs text-red-300">
                    <strong className="text-red-200">Duplicate user accounts:</strong> {state.duplicate_user_accounts_count} accounts share this email (different casing or pre-normalization legacy). Below are all of them — products + the founder flag may be split between them.
                  </div>
                </div>
                <ul className="text-xs space-y-1 ml-6" data-testid="duplicate-user-accounts-list">
                  {state.duplicate_user_accounts?.map((u) => (
                    <li key={u.user_id} className="flex flex-wrap justify-between gap-2 py-1 border-b border-white/5 last:border-0">
                      <span className="text-white font-mono break-all">
                        {u.email}
                      </span>
                      <span className="text-neutral-400">
                        {u.products_count} products
                        {u.is_founding_creator && <span className="text-yellow-300 ml-1">· founder</span>}
                        {u.created_at && <span className="text-neutral-600 ml-1">· {new Date(u.created_at).toLocaleDateString()}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Field label="Name" value={state.user?.name} />
            <Field label="Email" value={state.user?.email} mono />
            <Field label="User ID" value={state.user?.user_id} mono />
            <Field label="Role" value={state.user?.role} ok={state.user?.role === 'creator'} warn={state.user?.role !== 'creator'} />
            <Field
              label="Founding Creator"
              value={state.user?.is_founding_creator}
              ok={!!state.user?.is_founding_creator}
              warn={!state.user?.is_founding_creator}
            />
            {state.user?.is_founding_creator && (
              <>
                <Field label="Founder Number" value={`#${state.user?.founding_creator_number || '?'}`} ok />
                <Field label="Activated At" value={state.user?.founding_creator_activated_at} />
              </>
            )}
            <Field label="VIP Verified" value={state.user?.vip_creator} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-2">Stripe (Live)</h4>
            {state.stripe_live ? (
              <>
                <Field label="Stripe Account ID" value={state.stripe_live.id} mono />
                {state.stripe_live.error ? (
                  <Field label="Stripe Error" value={state.stripe_live.error} warn />
                ) : (
                  <>
                    <Field
                      label="Charges Enabled"
                      value={state.stripe_live.charges_enabled}
                      ok={state.stripe_live.charges_enabled === true}
                      warn={state.stripe_live.charges_enabled === false}
                    />
                    <Field
                      label="Payouts Enabled"
                      value={state.stripe_live.payouts_enabled}
                      ok={state.stripe_live.payouts_enabled === true}
                      warn={state.stripe_live.payouts_enabled === false}
                    />
                    <Field label="Details Submitted" value={state.stripe_live.details_submitted} />
                    {state.stripe_live.requirements_currently_due?.length > 0 && (
                      <Field
                        label="Stripe Needs Now"
                        value={state.stripe_live.requirements_currently_due.join(', ')}
                        warn
                      />
                    )}
                    {state.stripe_live.requirements_disabled_reason && (
                      <Field label="Stripe Disabled Reason" value={state.stripe_live.requirements_disabled_reason} warn />
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="text-sm text-neutral-500 italic">No Stripe account connected yet.</div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-2">Store</h4>
            {state.creator_profiles_count > 1 && (
              <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <div className="text-xs text-red-300">
                  <strong className="text-red-200">Data quality issue:</strong> This user has{' '}
                  <strong>{state.creator_profiles_count} creator profiles</strong> in the
                  database (should be exactly 1). Likely from a failed Stripe-Connect
                  retry. Products are counted across ALL of them so the count is correct,
                  but the profile shown below is just the first one. Admin cleanup recommended.
                </div>
              </div>
            )}
            <Field label="Store Name" value={state.creator_profile?.store_name} />
            <Field label="Store Slug" value={state.creator_profile?.store_slug} mono />
            <Field label="Products" value={state.products_count} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-2">Platform Fee Right Now</h4>
            <Field
              label="Current Tier"
              value={state.fee_snapshot?.tier}
              ok={state.fee_snapshot?.tier === 'founding'}
              warn={state.fee_snapshot?.tier === 'standard' && state.user?.is_founding_creator}
            />
            <Field label="Current Rate" value={`${(state.fee_snapshot?.rate * 100 || 0).toFixed(1)}%`} />
            <Field label="Tier Expires" value={state.fee_snapshot?.expires_at} />
            {/* Pending benefits — explains why a Founding Creator might
                still show 5% pre-launch ("0% scheduled to start on launch day"). */}
            {state.fee_snapshot?.pending?.length > 0 && (
              <div className="mt-2 bg-[#00FF94]/10 border border-[#00FF94]/30 rounded-lg p-3">
                <div className="text-xs font-semibold text-[#00FF94] mb-1.5">Scheduled Benefits (start later):</div>
                <ul className="text-xs text-neutral-300 space-y-1">
                  {state.fee_snapshot.pending.map((p, i) => (
                    <li key={i}>
                      <span className="text-[#00FF94] font-medium">{p.tier === 'founding' ? 'Founding Creator (0%)' : p.tier}</span>
                      {' — '}
                      {p.status === 'scheduled' && p.starts_at && `starts ${new Date(p.starts_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`}
                      {p.status === 'paused_pre_launch' && 'paused (launch date not set)'}
                      {p.status === 'expired' && 'expired'}
                      {p.status === 'paused' && 'paused (better benefit active)'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function HouseProductsHeroSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/house-products/hero-status`, {
        headers: authHeaders(),
      });
      setStatus(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Hero status check failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const runRegen = async () => {
    if (!window.confirm(
      `This will regenerate hero images for ${status?.missing_image || 0} house product(s) using Nano Banana (spends LLM credits). Continue?`
    )) return;
    setRegenLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/house-products/regenerate-missing-heroes`, {}, {
        headers: authHeaders(),
        timeout: 120000,
      });
      toast.success(`Regenerated ${res.data?.shipped || 0} hero image(s)`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regen failed');
    } finally {
      setRegenLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, [loadStatus]);

  return (
    <Card
      title="House Products — Hero Image Health"
      subtitle="House products that ship without a hero image fall back to a generic placeholder in the weekly digest email and on the /weekly page. This panel tells you which products are missing one and (one click) re-runs the Nano Banana image generator on them."
      icon={ImageIcon}
      accent="#00FF94"
    >
      {loading && !status ? (
        <div className="flex items-center gap-2 text-neutral-400 py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : status ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
            <StatPill label="House Products" value={status.total_house_products} color="info" />
            <StatPill
              label="Have Hero ✓"
              value={(status.total_house_products || 0) - (status.missing_image || 0)}
              color="good"
            />
            <StatPill
              label="Missing Hero"
              value={status.missing_image}
              color={status.missing_image > 0 ? 'warn' : 'good'}
            />
          </div>

          {status.missing_image > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
              <ul className="text-xs text-neutral-300 space-y-1 max-h-40 overflow-y-auto">
                {status.missing_items?.map((it) => (
                  <li key={it.product_id} className="flex justify-between gap-2 py-1 border-b border-white/5 last:border-0">
                    <span className="text-white font-medium truncate">{it.name}</span>
                    <span className="text-neutral-500 shrink-0 font-mono text-[10px]">{it.slug}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {status.missing_image > 0 && (
              <Button
                onClick={runRegen}
                disabled={regenLoading}
                className="bg-[#00FF94] hover:bg-[#00C277] text-black font-bold"
                data-testid="regen-heroes-btn"
              >
                {regenLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                Regenerate {status.missing_image} Hero{status.missing_image === 1 ? '' : 'es'}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={loadStatus}
              disabled={loading}
              className="border-white/20"
            >
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </>
      ) : null}
    </Card>
  );
}

function PendingPayoutsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryingAll, setRetryingAll] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/pending-payouts?status_filter=pending`, {
        headers: authHeaders(),
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pending payouts fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const retryAll = async () => {
    if (!window.confirm(
      `Retry ALL ${data?.counts?.pending || 0} pending payout(s)? This re-checks each creator's Stripe status and pushes the transfer if they're now verified.`
    )) return;
    setRetryingAll(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/pending-payouts/retry-now`, {}, {
        headers: authHeaders(),
      });
      toast.success(`Retry done: ${res.data.succeeded || 0} succeeded · ${res.data.still_restricted || 0} still restricted · ${res.data.errors || 0} errors`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry-all failed');
    } finally {
      setRetryingAll(false);
    }
  };

  const retryOne = async (pid) => {
    setRetryingId(pid);
    try {
      const res = await axios.post(`${API_URL}/api/admin/pending-payouts/${pid}/retry`, {}, {
        headers: authHeaders(),
      });
      if (res.data?.result === 'transferred') {
        toast.success(`Transferred! ID: ${res.data.transfer_id}`);
      } else if (res.data?.result === 'still_restricted') {
        toast.warning('Creator Stripe is still restricted — they haven\'t finished onboarding yet.');
      } else {
        toast.error(`Retry result: ${res.data?.result || 'unknown'}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  useEffect(() => { load(); }, [load]);

  const pendingCount = data?.counts?.pending || 0;

  return (
    <Card
      title="Pending Creator Payouts"
      subtitle="When a buyer pays but the creator's Stripe account is restricted, the payment lands in our platform balance and we queue the creator's earnings here. An hourly cron retries automatically once the creator finishes Stripe setup. Use 'Retry All' to push immediately after you confirm a creator is verified."
      icon={DollarSign}
      accent="#FF6B6B"
    >
      {loading && !data ? (
        <div className="flex items-center gap-2 text-neutral-400 py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <StatPill
              label="Pending"
              value={pendingCount}
              color={pendingCount > 0 ? 'warn' : 'good'}
            />
            <StatPill label="Succeeded (all-time)" value={data.counts?.succeeded ?? 0} color="good" />
            <StatPill
              label="Abandoned (30+ retries)"
              value={data.counts?.abandoned ?? 0}
              color={data.counts?.abandoned > 0 ? 'bad' : 'neutral'}
            />
          </div>

          {pendingCount === 0 ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
              <div className="text-sm text-green-300">
                No pending creator payouts. All Stripe transfers are flowing cleanly.
                <Button
                  variant="outline"
                  size="sm"
                  onClick={load}
                  className="ml-3 border-white/20 text-xs"
                >
                  Refresh
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                <div className="text-xs text-neutral-300 max-h-64 overflow-y-auto" data-testid="pending-payouts-list">
                  <table className="w-full text-left">
                    <thead className="text-[10px] uppercase tracking-wider text-neutral-500">
                      <tr>
                        <th className="py-1.5">Product</th>
                        <th className="py-1.5">Amount</th>
                        <th className="py-1.5">Attempts</th>
                        <th className="py-1.5">Last Error</th>
                        <th className="py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows?.map((r) => (
                        <tr key={r.pending_payout_id} className="border-t border-white/5">
                          <td className="py-2 pr-2">
                            <div className="text-white font-medium truncate max-w-[160px]">{r.product_name || '—'}</div>
                            <div className="text-neutral-500 text-[10px] truncate max-w-[160px]">{r.order_id}</div>
                          </td>
                          <td className="py-2 pr-2 text-[#00F0FF] font-mono">${(r.amount_usd || 0).toFixed(2)}</td>
                          <td className="py-2 pr-2">{r.attempts || 1}</td>
                          <td className="py-2 pr-2 text-[10px] text-neutral-500 truncate max-w-[160px]" title={r.last_error}>
                            {r.last_error || '—'}
                          </td>
                          <td className="py-2 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => retryOne(r.pending_payout_id)}
                              disabled={retryingId === r.pending_payout_id}
                              className="border-white/20 text-xs h-7"
                              data-testid={`retry-payout-${r.pending_payout_id}`}
                            >
                              {retryingId === r.pending_payout_id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : 'Retry'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={retryAll}
                  disabled={retryingAll}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                  data-testid="retry-all-payouts-btn"
                >
                  {retryingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                  Retry All ({pendingCount})
                </Button>
                <Button
                  variant="outline"
                  onClick={load}
                  disabled={loading}
                  className="border-white/20"
                >
                  <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </>
          )}
        </>
      ) : null}
    </Card>
  );
}

function DuplicateEmailUsersSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/duplicate-email-users`, {
        headers: authHeaders(),
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate-email scan failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { scan(); }, [scan]);

  const groupCount = data?.duplicate_groups_count || 0;

  return (
    <Card
      title="Duplicate User Accounts"
      subtitle="Scans every user account for groups that share the same email when compared case-insensitively. Catches the Edward-style bug where 'Ek4011@gmail.com' and 'ek4011@gmail.com' exist as two separate accounts. Merge them manually — keep the one with the products & founder flag, then archive the orphan."
      icon={Search}
      accent="#FF6B6B"
    >
      {loading && !data ? (
        <div className="flex items-center gap-2 text-neutral-400 py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Scanning…
        </div>
      ) : data ? (
        <>
          <div className="mb-4">
            <StatPill
              label="Duplicate Email Groups"
              value={groupCount}
              color={groupCount > 0 ? 'bad' : 'good'}
            />
          </div>

          {groupCount === 0 ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
              <div className="text-sm text-green-300">
                No duplicate-email accounts found. All user emails are unique (case-insensitive).
              </div>
            </div>
          ) : (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
              <ul className="text-xs space-y-3 max-h-96 overflow-y-auto" data-testid="duplicate-email-groups-list">
                {data.groups?.map((g) => (
                  <li key={g.email_lower} className="border-b border-white/5 last:border-0 pb-3 last:pb-0">
                    <div className="text-white font-semibold text-sm mb-1">{g.email_lower} <span className="text-red-300 font-normal">· {g.user_count} accounts</span></div>
                    <table className="w-full text-[11px]">
                      <thead className="text-neutral-500">
                        <tr>
                          <th className="text-left py-1">Email (actual casing)</th>
                          <th className="text-left py-1">user_id</th>
                          <th className="text-left py-1">Role</th>
                          <th className="text-left py-1">Founder?</th>
                          <th className="text-left py-1">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.users?.map((u) => (
                          <tr key={u.user_id} className="border-t border-white/5">
                            <td className="py-1 font-mono text-white break-all">{u.email}</td>
                            <td className="py-1 font-mono text-neutral-400 text-[10px]">{u.user_id}</td>
                            <td className="py-1 text-neutral-300">{u.role || '—'}</td>
                            <td className="py-1">{u.is_founding_creator ? <span className="text-yellow-300">yes</span> : <span className="text-neutral-600">—</span>}</td>
                            <td className="py-1 text-neutral-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            variant="outline"
            onClick={scan}
            disabled={loading}
            className="border-white/20"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Re-scan
          </Button>
        </>
      ) : null}
    </Card>
  );
}

export default function AdminCreatorHealth() {
  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto py-6 md:py-10 px-4 md:px-6 space-y-5 md:space-y-6">
        <div className="flex items-start gap-3 mb-2">
          <ShieldAlert className="w-7 h-7 text-[#00F0FF] mt-1" />
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Creator Health
            </h1>
            <p className="text-neutral-400 text-sm md:text-base mt-1">
              One-click diagnostics + healing for VIP creators, Stripe state, and weekly digest images. Replaces the old "go hit /api/admin/foo in your browser" workflow.
            </p>
          </div>
        </div>

        <FoundingCreatorSection />
        <DuplicateEmailUsersSection />
        <PendingPayoutsSection />
        <CreatorDiagnosticSection />
        <HouseProductsHeroSection />
      </div>
    </AdminLayout>
  );
}
